import { useEffect, useState } from "react";
import { getDueCards, reviewCard, type DueCard } from "../lib/api";

interface Props {
  onDone: () => void;
  // If true: hide "Skip" button; user must finish review (used when launched
  // from "1 due word" required-flow). Default false.
  required?: boolean;
}

const RATING_BUTTONS: Array<{
  rating: 1 | 2 | 3 | 4;
  label: string;
  hint: string;
  cls: string;
}> = [
  { rating: 1, label: "Again",  hint: "didn't know it", cls: "bg-rose-600/80 hover:bg-rose-500" },
  { rating: 2, label: "Hard",   hint: "barely",         cls: "bg-amber-600/80 hover:bg-amber-500" },
  { rating: 3, label: "Good",   hint: "got it",         cls: "bg-emerald-600/80 hover:bg-emerald-500" },
  { rating: 4, label: "Easy",   hint: "trivial",        cls: "bg-sky-600/80 hover:bg-sky-500" },
];

export function VocabReview({ onDone, required = false }: Props): JSX.Element {
  const [cards, setCards] = useState<DueCard[] | null>(null);
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDueCards(15)
      .then((c) => setCards(c))
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  if (error) {
    return (
      <div className="rounded-2xl border border-rose-700/40 bg-rose-500/10 p-5 text-sm text-rose-200">
        {error}
      </div>
    );
  }

  if (cards === null) {
    return (
      <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-8 text-center text-sm text-ink-400">
        Loading review queue…
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="rounded-2xl border border-emerald-700/40 bg-emerald-500/5 p-6 text-center">
        <div className="text-3xl mb-2">✓</div>
        <div className="text-white font-medium">No words due right now.</div>
        <p className="text-sm text-ink-400 mt-1">
          Have a conversation with Arthur and new words will show up here.
        </p>
        <button
          onClick={onDone}
          className="mt-4 rounded-lg bg-lime-500 hover:bg-lime-400 text-ink-900 font-medium px-4 py-2 text-sm"
        >
          Start lesson
        </button>
      </div>
    );
  }

  if (idx >= cards.length) {
    return (
      <div className="rounded-2xl border border-lime-700/40 bg-lime-500/5 p-6 text-center">
        <div className="text-3xl mb-2">{"🎉"}</div>
        <div className="text-white font-medium">Review done — {cards.length} cards</div>
        <p className="text-sm text-ink-400 mt-1">
          Next round will appear when these words are due again.
        </p>
        <button
          onClick={onDone}
          className="mt-4 rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-5 py-2.5"
        >
          Continue to lesson
        </button>
      </div>
    );
  }

  const card = cards[idx];

  async function handleRate(rating: 1 | 2 | 3 | 4): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    try {
      await reviewCard(card.id, rating);
      setIdx(idx + 1);
      setRevealed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-6">
      <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-ink-500 mb-3">
        <span>Word review</span>
        <span>
          {idx + 1} / {cards.length}
        </span>
      </div>

      <div className="rounded-xl border border-ink-700 bg-ink-800/60 px-5 py-8 text-center min-h-[180px] flex flex-col items-center justify-center">
        <div className="text-3xl font-semibold text-white">{card.lemma}</div>
        {revealed && (
          <>
            {card.glossPl && (
              <div className="mt-3 text-sm text-lime-300">{card.glossPl}</div>
            )}
            {card.example && (
              <div className="mt-2 text-sm italic text-ink-300">{"“"}{card.example}{"”"}</div>
            )}
          </>
        )}
        {!revealed && (
          <div className="mt-3 text-xs text-ink-500">Recall the meaning, then tap below.</div>
        )}
      </div>

      {!revealed ? (
        <button
          onClick={() => setRevealed(true)}
          className="mt-4 w-full rounded-xl bg-ink-700 hover:bg-ink-600 text-white font-medium py-3"
        >
          Show answer
        </button>
      ) : (
        <div className="mt-4 grid grid-cols-4 gap-2">
          {RATING_BUTTONS.map((b) => (
            <button
              key={b.rating}
              onClick={() => handleRate(b.rating)}
              disabled={submitting}
              className={`rounded-xl text-white font-medium py-2.5 text-sm disabled:opacity-50 transition ${b.cls}`}
            >
              <div>{b.label}</div>
              <div className="text-[10px] opacity-80">{b.hint}</div>
            </button>
          ))}
        </div>
      )}

      {!required && (
        <button
          onClick={onDone}
          className="mt-3 w-full text-xs text-ink-400 hover:text-ink-200 underline-offset-4 hover:underline"
        >
          Skip review for now
        </button>
      )}
    </div>
  );
}
